import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

import { COLORS } from "./dioramaLayout";

function createZTexture(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = COLORS.purple;
  ctx.font = "bold 24px sans-serif";
  ctx.fillText("z", 8, 24);
  return new THREE.CanvasTexture(canvas);
}

/** Visual-only sleep patch — reusable outside the engine's InteractiveProp wrapper. */
export function SleepPatchModel() {
  const spriteRefs = useRef<(THREE.Sprite | null)[]>([null, null, null]);

  const zTexture = useMemo(createZTexture, []);

  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    for (let i = 0; i < 3; i++) {
      const sprite = spriteRefs.current[i];
      if (!sprite) continue;
      sprite.position.y = 0.6 + i * 0.4 + Math.sin(t * 0.8 + i) * 0.15;
      (sprite.material as THREE.SpriteMaterial).opacity =
        0.3 + Math.sin(t + i * 1.5) * 0.25;
    }
  });

  return (
    <group>
      {/* Sand mound */}
      <mesh position={[0, 0.075, 0]} receiveShadow>
        <cylinderGeometry args={[1.0, 1.2, 0.15, 8]} />
        <meshStandardMaterial color={COLORS.sandDark} flatShading />
      </mesh>

      {/* Raised center */}
      <mesh position={[0, 0.05, 0]} scale={[1, 0.12, 1]}>
        <sphereGeometry args={[0.8, 6, 4]} />
        <meshStandardMaterial color={COLORS.sand} flatShading />
      </mesh>

      {/* Pillow */}
      <mesh position={[-0.2, 0.18, -0.1]} rotation={[0, 0.3, 0]} castShadow>
        <boxGeometry args={[0.5, 0.12, 0.35]} />
        <meshStandardMaterial color={COLORS.pink} flatShading />
      </mesh>

      {/* Floating Zzz */}
      {[0, 1, 2].map((i) => (
        <sprite
          key={i}
          ref={(el) => { spriteRefs.current[i] = el; }}
          position={[0.2 + i * 0.25, 0.6 + i * 0.4, 0]}
          scale={[0.3 + i * 0.1, 0.3 + i * 0.1, 1]}
        >
          <spriteMaterial map={zTexture} transparent opacity={0.6} />
        </sprite>
      ))}
    </group>
  );
}

// The engine wrapper was retired when the nap spot became the Notebook prop
// (OfficeNotebook). SleepPatchModel stays for the landing page's HeroWorld.
